import React, { memo, PropsWithChildren, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Box } from '../../../../../Box';
import { Tooltip } from '../../../../../Tooltip';
import { dataTableClassnames } from '../../../const/classNames';
import { Typography } from '../../../../../Typography/Typography';

interface CellContentProps {
  before?: React.ReactNode;
  after?: React.ReactNode;
  dataTest: string | undefined;
  style: React.CSSProperties | undefined;
  ellipsisOnOverflow?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export const CellContent: React.FC<PropsWithChildren<CellContentProps>> = memo(
  ({ before, after, children, dataTest, style, ellipsisOnOverflow = true, align, className }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [enableTooltip, setEnableTooltip] = useState(false);
    return (
      <Box
        justifyContent={align}
        dataTest={dataTest}
        style={style}
        className={`${dataTableClassnames.table.cell.content} ${className ?? ''}`}
      >
        {!!before && (
          <Box display="inline-flex" flexShrink={0}>
            {before}
          </Box>
        )}
        {ellipsisOnOverflow ? (
          <Box component="span" overflow="hidden" textOverflow="ellipsis" ref={ref} whiteSpace="nowrap">
            <Typography variant="body">
              <Tooltip
                title={<Box sx={{ a: { color: `${tokens.colorOnSecondary} !important` } }}>{children}</Box>}
                disabled={!enableTooltip}
              >
                <Box
                  data-content-parent
                  onMouseEnter={(e) => {
                    setEnableTooltip(
                      e.currentTarget.offsetWidth < (e.currentTarget.children[0] as HTMLSpanElement)?.offsetWidth
                    );
                  }}
                >
                  <span data-content-children>{children}</span>
                </Box>
              </Tooltip>
            </Typography>
          </Box>
        ) : (
          <Box component="span" overflow="hidden">
            <Typography variant="body">{children}</Typography>
          </Box>
        )}

        {!!after && (
          <Box display="inline-flex" flexShrink={0}>
            {after}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
